import type { ArchiveTagCensus, KnownPairSummary } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { SequenceRead } from '../../Util/orderSettingDetection/sequenceHead.ts'
import { parseSequenceHead } from '../../Util/orderSettingDetection/sequenceHead.ts'
import { decodeHead } from '../../Util/sequencingPlatformDetection.ts'
import { bestPrefixEditDistance, toMasks } from '../../Util/orderSettingDetection/iupac.ts'
import { stripAdapter } from '../../Util/orderSettingDetection/primerRegistry.ts'
import { r1OpensWithSecond } from './estimationCore.ts'

/** Must stay free of DOM, store and path-alias imports: it runs inside the detection worker. */

export type CensusRequest = { pair: KnownPairSummary; archives: { key: string; forward: Blob; reverse: Blob }[] }
export type CensusResult = { censuses: { key: string; census: ArchiveTagCensus }[] }

/** More than the classifier's head: an archive spreads its reads over many samples. */
const CENSUS_BYTES = 1024 * 1024
const CENSUS_READS = 1000
const MAX_PREFIX = 24
const ERROR_RATE = 0.15
const MIN_SHARE = 0.01

type Gene = { masks: number[]; maxErrors: number }

const gene = (primer: string): Gene => {
  const masks = toMasks(stripAdapter(primer))
  return { masks, maxErrors: Math.floor(masks.length * ERROR_RATE) }
}

/** The bases before the gene primer's best match within the read's first bases; `null` when it is not there. */
const prefixBefore = (read: string, primer: Gene): string | null => {
  let best: { start: number; distance: number } | null = null
  for (let start = 0; start <= MAX_PREFIX && start + primer.masks.length <= read.length; start++) {
    const distance = bestPrefixEditDistance(primer.masks, read.slice(start), 0, primer.maxErrors)
    if (distance <= primer.maxErrors && (!best || distance < best.distance)) {
      best = { start, distance }
    }
    if (best?.distance === 0) {
      break
    }
  }
  return best ? read.slice(0, best.start) : null
}

const readId = (read: SequenceRead) => (read.header.split(/\s+/)[0] ?? '').replace(/\/[12]$/, '')

/**
 * The tag pairs an archive's read pairs carry: for each mate pair, the bases in front of the forward gene
 * primer on R1 and of the reverse one on R2. Experimental and for eyeballing a sheet's completeness only.
 */
export const archiveTagCensus = (r1: readonly SequenceRead[], r2: readonly SequenceRead[], pair: KnownPairSummary): ArchiveTagCensus => {
  const swapped = r1OpensWithSecond(r1, pair)
  const forward = gene(swapped ? pair.second : pair.first)
  const reverse = gene(swapped ? pair.first : pair.second)
  const counts = new Map<string, { forwardTag: string; reverseTag: string; count: number }>()
  let pairs = 0
  let located = 0
  for (let i = 0; i < Math.min(r1.length, r2.length); i++) {
    if (readId(r1[i]) !== readId(r2[i])) {
      continue
    }
    pairs++
    const forwardTag = prefixBefore(r1[i].sequence, forward)
    const reverseTag = prefixBefore(r2[i].sequence, reverse)
    if (forwardTag === null || reverseTag === null) {
      continue
    }
    located++
    const key = `${forwardTag}+${reverseTag}`
    const entry = counts.get(key) ?? { forwardTag, reverseTag, count: 0 }
    entry.count++
    counts.set(key, entry)
  }
  const tags = [...counts.values()]
    .map(({ forwardTag, reverseTag, count }) => ({ forwardTag, reverseTag, share: located === 0 ? 0 : count / located }))
    .filter((tag) => tag.share >= MIN_SHARE)
    .sort((a, b) => b.share - a.share)
  return { pairs, located, tags }
}

const readHead = async (blob: Blob) => parseSequenceHead(await decodeHead(blob, CENSUS_BYTES), CENSUS_READS)

export const runCensus = async (request: CensusRequest): Promise<CensusResult> => {
  const censuses: CensusResult['censuses'] = []
  for (const archive of request.archives) {
    const [r1, r2] = await Promise.all([readHead(archive.forward), readHead(archive.reverse)])
    censuses.push({ key: archive.key, census: archiveTagCensus(r1, r2, request.pair) })
  }
  return { censuses }
}
